import { useMemo } from "react";
import { CHART_CONFIG } from "../utils/chartTypes";
import useIsMobile from "./useIsMobile";

type AxisDomain = [number, number] | [string, string];

interface ValueAxisConfig {
    domain?: AxisDomain;
    ticks?: number[];
}

interface UseChartOrientationParams {
    orientation: 0 | 1 | null | undefined;
    dataKey?: string;
    valueAxis: ValueAxisConfig;
    categoryPadding?: number;
}

const axisStyle = {
    stroke: CHART_CONFIG.STROKE_COLOR,
    tick: { fontSize: CHART_CONFIG.TICK_FONT_SIZE },
};

const MOBILE_VALUE_AXIS_FONT_SIZE = 8;
const MAX_DIGITS_BEFORE_SHRINK = 4;

const hasLargeValues = ({ domain, ticks }: ValueAxisConfig): boolean => {
    const numbers = [
        ...(ticks ?? []),
        ...((domain ?? []).filter((v): v is number => typeof v === "number")),
    ];
    return numbers.some((value) => Math.trunc(Math.abs(value)).toString().length > MAX_DIGITS_BEFORE_SHRINK);
};

const formatLargeNumber = (value: number): string => {
    const abs = Math.abs(value);
    const format = (divisor: number, suffix: string) => {
        const short = value / divisor;
        return `${Number.isInteger(short) ? short : short.toFixed(1)}${suffix}`;
    };
    if (abs >= 1_000_000_000) return format(1_000_000_000, "B");
    if (abs >= 1_000_000) return format(1_000_000, "M");
    if (abs >= 10_000) return format(1_000, "k");
    return value.toString();
};

export const useChartOrientation = ({ orientation, dataKey = "name", valueAxis }: UseChartOrientationParams) => {
    const isMobile = useIsMobile();

    return useMemo(() => {
        // orientation (negocio): 0/null = Vertical (barras paradas), 1 = Horizontal (barras acostadas).
        // Recharts nombra su prop "layout" al revés de esto: layout="horizontal" (default) => barras paradas,
        // layout="vertical" => barras acostadas
        const isVertical = orientation === 1;
        // En mobile, si el eje de valores queda horizontal (orientation === 1), los números siempre
        // se achican por modelo de negocio, además de achicarse cuando son valores muy largos.
        const shrinkValueTicks = isMobile && (isVertical || hasLargeValues(valueAxis));

        const categoryAxisProps = {
            ...axisStyle,
            type: "category" as const,
            dataKey,
            reversed: isVertical,
        };

        const valueAxisProps = {
            ...axisStyle,
            tick: { fontSize: shrinkValueTicks ? MOBILE_VALUE_AXIS_FONT_SIZE : CHART_CONFIG.TICK_FONT_SIZE },
            tickFormatter: shrinkValueTicks ? formatLargeNumber : undefined,
            type: "number" as const,
            domain: valueAxis.domain,
            ticks: valueAxis.ticks,
            allowDecimals: false,
            interval: 0,
        };

        return {
            layout: (isVertical ? "vertical" : "horizontal") as "horizontal" | "vertical",
            xAxisProps: isVertical ? valueAxisProps : categoryAxisProps,
            yAxisProps: isVertical ? categoryAxisProps : valueAxisProps,
            referenceLineProps: isVertical ? { x: 0 } : { y: 0 },
        };
    }, [orientation, dataKey, valueAxis.domain, valueAxis.ticks, isMobile]);
};
